// LocalStorage utility for saving and loading queries

const STORAGE_KEY = 'stop_saved_queries';
const MAX_QUERIES = 50;

// Generate unique query ID
const generateQueryId = () => {
  return `query_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get all saved queries from localStorage
export const getSavedQueries = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }
    const queries = JSON.parse(stored);
    return Array.isArray(queries) ? queries : [];
  } catch (error) {
    console.error('Error loading saved queries:', error);
    return [];
  }
};

// Save a new query or update existing
export const saveQuery = (queryData) => {
  try {
    const queries = getSavedQueries();
    const now = new Date().toISOString();

    // Check if updating existing query
    const existingIndex = queries.findIndex(q => q.id === queryData.id);

    if (existingIndex >= 0) {
      // Update existing query
      queries[existingIndex] = {
        ...queries[existingIndex],
        ...queryData,
        modifiedAt: now
      };
    } else {
      // Create new query
      const newQuery = {
        id: generateQueryId(),
        name: queryData.name,
        selectedFields: queryData.selectedFields,
        isFavorite: queryData.isFavorite || false,
        createdAt: now,
        modifiedAt: now
      };

      // Check query limit
      if (queries.length >= MAX_QUERIES) {
        // Remove oldest non-favorite query
        const nonFavorites = queries.filter(q => !q.isFavorite);
        if (nonFavorites.length > 0) {
          nonFavorites.sort((a, b) => new Date(a.modifiedAt) - new Date(b.modifiedAt));
          const toRemove = nonFavorites[0];
          const removeIndex = queries.findIndex(q => q.id === toRemove.id);
          queries.splice(removeIndex, 1);
        } else {
          throw new Error('Maximum number of saved queries reached. Please delete some queries to save new ones.');
        }
      }

      queries.push(newQuery);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(queries));
    return { success: true, query: queries[existingIndex] || queries[queries.length - 1] };
  } catch (error) {
    console.error('Error saving query:', error);
    return { success: false, error: error.message };
  }
};

// Delete a query by ID
export const deleteQuery = (queryId) => {
  try {
    const queries = getSavedQueries();
    const filtered = queries.filter(q => q.id !== queryId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    return { success: true };
  } catch (error) {
    console.error('Error deleting query:', error);
    return { success: false, error: error.message };
  }
};

// Get a single query by ID
export const getQueryById = (queryId) => {
  const queries = getSavedQueries();
  return queries.find(q => q.id === queryId);
};

// Rename a query
export const renameQuery = (queryId, newName) => {
  try {
    const queries = getSavedQueries();
    const index = queries.findIndex(q => q.id === queryId);

    if (index === -1) {
      return { success: false, error: 'Query not found' };
    }

    queries[index] = {
      ...queries[index],
      name: newName,
      modifiedAt: new Date().toISOString()
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(queries));
    return { success: true, query: queries[index] };
  } catch (error) {
    console.error('Error renaming query:', error);
    return { success: false, error: error.message };
  }
};

// Duplicate a query
export const duplicateQuery = (queryId) => {
  try {
    const queries = getSavedQueries();
    const original = queries.find(q => q.id === queryId);

    if (!original) {
      return { success: false, error: 'Query not found' };
    }

    // Check query limit
    if (queries.length >= MAX_QUERIES) {
      return { success: false, error: 'Maximum number of saved queries reached' };
    }

    const now = new Date().toISOString();
    const duplicate = {
      ...original,
      id: generateQueryId(),
      name: `${original.name} (Copy)`,
      isFavorite: false,
      createdAt: now,
      modifiedAt: now
    };

    queries.push(duplicate);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queries));
    return { success: true, query: duplicate };
  } catch (error) {
    console.error('Error duplicating query:', error);
    return { success: false, error: error.message };
  }
};

// Toggle favorite status
export const toggleFavorite = (queryId) => {
  try {
    const queries = getSavedQueries();
    const index = queries.findIndex(q => q.id === queryId);

    if (index === -1) {
      return { success: false, error: 'Query not found' };
    }

    queries[index] = {
      ...queries[index],
      isFavorite: !queries[index].isFavorite,
      modifiedAt: new Date().toISOString()
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(queries));
    return { success: true, query: queries[index] };
  } catch (error) {
    console.error('Error toggling favorite:', error);
    return { success: false, error: error.message };
  }
};

// Get sorted queries (favorites first, then by modified date)
export const getSortedQueries = () => {
  const queries = getSavedQueries();
  return queries.sort((a, b) => {
    // Favorites first
    if (a.isFavorite && !b.isFavorite) return -1;
    if (!a.isFavorite && b.isFavorite) return 1;

    // Then by modified date (most recent first)
    return new Date(b.modifiedAt) - new Date(a.modifiedAt);
  });
};

// Validate query structure
export const validateQuery = (query) => {
  if (!query || typeof query !== 'object') {
    return { valid: false, error: 'Invalid query format' };
  }

  if (!query.name || typeof query.name !== 'string') {
    return { valid: false, error: 'Query name is required' };
  }

  if (!Array.isArray(query.selectedFields)) {
    return { valid: false, error: 'Query must have selectedFields array' };
  }

  return { valid: true };
};

// Export all queries as JSON
export const exportQueries = () => {
  const queries = getSavedQueries();
  const dataStr = JSON.stringify(queries, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `saved_queries_${Date.now()}.json`;
  link.click();
  URL.revokeObjectURL(url);
};

// Import queries from JSON
export const importQueries = (jsonData) => {
  try {
    const imported = JSON.parse(jsonData);
    if (!Array.isArray(imported)) {
      return { success: false, error: 'Invalid format: expected array of queries' };
    }

    const currentQueries = getSavedQueries();
    const merged = [...currentQueries];

    for (const query of imported) {
      const validation = validateQuery(query);
      if (validation.valid) {
        // Assign new ID to avoid conflicts
        const newQuery = {
          ...query,
          id: generateQueryId(),
          createdAt: new Date().toISOString(),
          modifiedAt: new Date().toISOString()
        };
        merged.push(newQuery);
      }
    }

    // Enforce limit
    if (merged.length > MAX_QUERIES) {
      merged.splice(MAX_QUERIES);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    return { success: true, count: imported.length };
  } catch (error) {
    console.error('Error importing queries:', error);
    return { success: false, error: error.message };
  }
};

// Get storage usage info
export const getStorageInfo = () => {
  const queries = getSavedQueries();
  return {
    count: queries.length,
    maxQueries: MAX_QUERIES,
    remaining: MAX_QUERIES - queries.length,
    favorites: queries.filter(q => q.isFavorite).length
  };
};
